/**
 * DashboardSkeleton — layout-stable placeholder for the whole dashboard.
 *
 * Mirrors the live layout so there's no reflow when data lands: 4 hero tiles,
 * the action card with 3 row ghosts, and the completed card. Animations use
 * the shared Skeleton primitive's `animate-pulse`; nothing is motion-heavy.
 */

import React from "react";

import { Card } from "@/components/shared/ui/card";
import { Skeleton } from "@/components/shared/ui/skeleton";

const HeroTileSkeleton: React.FC = () => (
  <Card density="comfortable" className="gap-4">
    <div className="flex items-start justify-between">
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-8 w-8 rounded-md" />
    </div>
    <div className="space-y-3">
      <Skeleton className="h-7 w-20" />
      <Skeleton className="h-3 w-40" />
    </div>
  </Card>
);

const ActionRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-4 rounded-md border border-border bg-card p-4">
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-3 w-32" />
    </div>
    <Skeleton className="h-5 w-24 rounded-full" />
    <Skeleton className="h-4 w-16" />
    <Skeleton className="h-9 w-20" />
  </div>
);

export const DashboardSkeleton: React.FC = () => (
  <div
    className="flex flex-col gap-6"
    role="status"
    aria-label="Loading dashboard"
    aria-busy="true"
  >
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <HeroTileSkeleton />
      <HeroTileSkeleton />
      <HeroTileSkeleton />
      <HeroTileSkeleton />
    </section>

    <Card density="default" className="gap-5">
      <div className="flex items-baseline justify-between">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="grid grid-cols-1 gap-3">
        <ActionRowSkeleton />
        <ActionRowSkeleton />
        <ActionRowSkeleton />
      </div>
    </Card>

    <Card density="default" className="gap-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-52" />
          </div>
        </div>
        <Skeleton className="h-4 w-16" />
      </div>
    </Card>

    <span className="sr-only">Loading dashboard data</span>
  </div>
);

export default DashboardSkeleton;
